import React, { Suspense, lazy, Component } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AppLayout } from '../layouts/AppLayout';
import { PageTransitionWrapper } from '../../shared/ui/PageTransitionWrapper';
import { ProtectedRoute } from './ProtectedRoute';
import { LoadingScreen } from '../../shared/ui/LoadingScreen';
import { Login } from '../../features/auth/pages/Login';
// Lazy-loaded feature pages. Named exports are mapped to default for lazy.
const lazyNamed = <T extends Record<string, unknown>,>(loader: () => Promise<T>, name: keyof T) => lazy(() => loader().then(m => ({
  default: m[name] as ComponentType<unknown>
})));
// analytics
const Overview = lazyNamed(() => import('../../features/analytics'), 'Overview');
const CustomMetrics = lazyNamed(() => import('../../features/analytics'), 'CustomMetrics');
const Nadi = lazyNamed(() => import('../../features/analytics'), 'Nadi');
const Reports = lazyNamed(() => import('../../features/analytics'), 'Reports');
const CardMonitoringPrograms = lazyNamed(() => import('../../features/analytics'), 'CardMonitoringPrograms');
const VampDetail = lazyNamed(() => import('../../features/analytics'), 'VampDetail');
const PaymentAnalytics = lazyNamed(() => import('../../features/analytics'), 'PaymentAnalytics');
// notifications
const Notifications = lazyNamed(() => import('../../features/notifications'), 'Notifications');
// payments
const PaymentRequests = lazyNamed(() => import('../../features/payments'), 'PaymentRequests');
const BulkPayments = lazyNamed(() => import('../../features/payments'), 'BulkPayments');
const BulkPaymentDetail = lazyNamed(() => import('../../features/payments'), 'BulkPaymentDetail');
const VirtualTerminal = lazyNamed(() => import('../../features/payments'), 'VirtualTerminal');
const QRCodes = lazyNamed(() => import('../../features/payments'), 'QRCodes');
// settings
const Settings = lazyNamed(() => import('../../features/settings'), 'Settings');
const UpdatePayoutAccount = lazyNamed(() => import('../../features/settings'), 'UpdatePayoutAccount');
// balance
const PayoutSettings = lazyNamed(() => import('../../features/balance'), 'PayoutSettings');
const BalanceSummary = lazyNamed(() => import('../../features/balance'), 'BalanceSummary');
const BalanceActivity = lazyNamed(() => import('../../features/balance'), 'BalanceActivity');
const BalanceActivityDetail = lazyNamed(() => import('../../features/balance'), 'BalanceActivityDetail');
const Payouts = lazyNamed(() => import('../../features/balance'), 'Payouts');
const PayoutDetail = lazyNamed(() => import('../../features/balance'), 'PayoutDetail');
// terminals
const PhysicalTerminals = lazyNamed(() => import('../../features/terminals'), 'PhysicalTerminals');
const SoftPosTerminals = lazyNamed(() => import('../../features/terminals'), 'SoftPosTerminals');
const Connect = lazyNamed(() => import('../../features/terminals'), 'Connect');
// developers
const WebhookEndpoints = lazyNamed(() => import('../../features/developers'), 'WebhookEndpoints');
const WebhookLogs = lazyNamed(() => import('../../features/developers'), 'WebhookLogs');
// automations
const AutomationsList = lazyNamed(() => import('../../features/automations'), 'AutomationsList');
const AutomationBuilder = lazyNamed(() => import('../../features/automations'), 'AutomationBuilder');
const AutomationTemplates = lazyNamed(() => import('../../features/automations'), 'AutomationTemplates');
const AutomationRuns = lazyNamed(() => import('../../features/automations'), 'AutomationRuns');
// tutorials
const Tutorials = lazyNamed(() => import('../../features/tutorials'), 'Tutorials');
const TutorialDetail = lazyNamed(() => import('../../features/tutorials'), 'TutorialDetail');
// shops (orders, shops, campaigns, sales, inventory)
const Orders = lazyNamed(() => import('../../features/shops'), 'Orders');
const OrderDetail = lazyNamed(() => import('../../features/shops'), 'OrderDetail');
const Shops = lazyNamed(() => import('../../features/shops'), 'Shops');
const ShopDetails = lazyNamed(() => import('../../features/shops'), 'ShopDetails');
const Campaigns = lazyNamed(() => import('../../features/shops'), 'Campaigns');
const CampaignDetails = lazyNamed(() => import('../../features/shops'), 'CampaignDetails');
const CampaignForm = lazyNamed(() => import('../../features/shops'), 'CampaignForm');
const Inventory = lazyNamed(() => import('../../features/shops'), 'Inventory');
const Sales = lazyNamed(() => import('../../features/shops'), 'Sales');
const SaleDetail = lazyNamed(() => import('../../features/shops'), 'SaleDetail');
const ShopSettings = lazyNamed(() => import('../../features/shops'), 'ShopSettings');
// shifts
const ShiftsList = lazyNamed(() => import('../../features/shifts'), 'ShiftsList');
const ShiftDetail = lazyNamed(() => import('../../features/shifts'), 'ShiftDetail');
const ProductDetail = lazyNamed(() => import('../../features/shops'), 'ProductDetail');
const ProductEdit = lazyNamed(() => import('../../features/shops'), 'ProductEdit');
// rooms
const PropertyInformation = lazyNamed(() => import('../../features/rooms'), 'PropertyInformation');
const Rooms = lazyNamed(() => import('../../features/rooms'), 'Rooms');
const AddRoom = lazyNamed(() => import('../../features/rooms'), 'AddRoom');
const EditRoom = lazyNamed(() => import('../../features/rooms'), 'EditRoom');
const RoomDetail = lazyNamed(() => import('../../features/rooms'), 'RoomDetail');
const Rates = lazyNamed(() => import('../../features/rooms'), 'Rates');
const Reservations = lazyNamed(() => import('../../features/rooms'), 'Reservations');
const ReservationDetail = lazyNamed(() => import('../../features/rooms'), 'ReservationDetail');
// invoices
const Invoices = lazyNamed(() => import('../../features/invoices'), 'Invoices');
const InvoiceDetail = lazyNamed(() => import('../../features/invoices'), 'InvoiceDetail');
const InvoiceForm = lazyNamed(() => import('../../features/invoices'), 'InvoiceForm');
// customers
const Customers = lazyNamed(() => import('../../features/customers'), 'Customers');
const CustomerDetail = lazyNamed(() => import('../../features/customers'), 'CustomerDetail');
const CustomerForm = lazyNamed(() => import('../../features/customers'), 'CustomerForm');
// suppliers
const Suppliers = lazyNamed(() => import('../../features/suppliers'), 'Suppliers');
const SupplierDetail = lazyNamed(() => import('../../features/suppliers'), 'SupplierDetail');
const SupplierForm = lazyNamed(() => import('../../features/suppliers'), 'SupplierForm');
const SupplierSelfService = lazyNamed(() => import('../../features/suppliers'), 'SupplierSelfService');
const BulkSupplierUpload = lazyNamed(() => import('../../features/suppliers'), 'BulkSupplierUpload');
// reconciliation
const Reconciliation = lazyNamed(() => import('../../features/reconciliation'), 'Reconciliation');
// bills
const Bills = lazyNamed(() => import('../../features/bills'), 'Bills');
const BillDetail = lazyNamed(() => import('../../features/bills'), 'BillDetail');
const BillForm = lazyNamed(() => import('../../features/bills'), 'BillForm');
const BillApprovalWizard = lazyNamed(() => import('../../features/bills'), 'BillApprovalWizard');
const BillFinancingWizard = lazyNamed(() => import('../../features/bills'), 'BillFinancingWizard');
const BulkBillUpload = lazyNamed(() => import('../../features/bills'), 'BulkBillUpload');
// disputes
const Disputes = lazyNamed(() => import('../../features/disputes'), 'Disputes');
const DisputeDetail = lazyNamed(() => import('../../features/disputes'), 'DisputeDetail');
const ChallengeDispute = lazyNamed(() => import('../../features/disputes'), 'ChallengeDispute');
// refunds
const Refunds = lazyNamed(() => import('../../features/refunds'), 'Refunds');
const RefundDetail = lazyNamed(() => import('../../features/refunds'), 'RefundDetail');
// system
const ActivityLogs = lazyNamed(() => import('../../features/system'), 'ActivityLogs');
const Requests = lazyNamed(() => import('../../features/system'), 'Requests');
const RequestDetail = lazyNamed(() => import('../../features/system'), 'RequestDetail');
export function AppRouter() {
  return <BrowserRouter data-id="element-239">
      <Suspense fallback={<LoadingScreen data-id="element-241" />} data-id="element-240">
        <Routes data-id="element-242">
          <Route path="/" element={<Login data-id="element-244" />} data-id="element-243" />
          <Route path="/supplier-onboarding/:id" element={<SupplierSelfService data-id="element-246" />} data-id="element-245" />

          <Route path="/overview" element={<ProtectedRoute data-id="element-248">
                <AppLayout data-id="element-249">
                  <PageTransitionWrapper data-id="element-250">
                    <Overview data-id="element-251" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-247" />
          <Route path="/notifications" element={<ProtectedRoute data-id="element-253">
                <AppLayout data-id="element-254">
                  <PageTransitionWrapper data-id="element-255">
                    <Notifications data-id="element-256" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-252" />
          <Route path="/payment-requests" element={<ProtectedRoute data-id="element-258">
                <AppLayout data-id="element-259">
                  <PageTransitionWrapper data-id="element-260">
                    <PaymentRequests data-id="element-261" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-257" />
          <Route path="/qr-codes" element={<ProtectedRoute data-id="element-263">
                <AppLayout data-id="element-264">
                  <PageTransitionWrapper data-id="element-265">
                    <QRCodes data-id="element-266" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-262" />
          <Route path="/bulk-payments" element={<ProtectedRoute data-id="element-268">
                <AppLayout data-id="element-269">
                  <PageTransitionWrapper data-id="element-270">
                    <BulkPayments data-id="element-271" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-267" />
          <Route path="/bulk-payment-detail/:id" element={<ProtectedRoute data-id="element-273">
                <AppLayout data-id="element-274">
                  <PageTransitionWrapper data-id="element-275">
                    <BulkPaymentDetail data-id="element-276" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-272" />
          <Route path="/settings" element={<ProtectedRoute data-id="element-278">
                <AppLayout data-id="element-279">
                  <PageTransitionWrapper data-id="element-280">
                    <Settings data-id="element-281" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-277" />
          <Route path="/settings/payouts" element={<ProtectedRoute data-id="element-283">
                <AppLayout data-id="element-284">
                  <PageTransitionWrapper data-id="element-285">
                    <PayoutSettings data-id="element-286" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-282" />
          <Route path="/settings/payout-account/edit" element={<ProtectedRoute data-id="element-288">
                <AppLayout data-id="element-289">
                  <PageTransitionWrapper data-id="element-290">
                    <UpdatePayoutAccount data-id="element-291" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-287" />
          <Route path="/virtual-terminal" element={<ProtectedRoute data-id="element-293">
                <AppLayout data-id="element-294">
                  <PageTransitionWrapper data-id="element-295">
                    <VirtualTerminal data-id="element-296" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-292" />
          <Route path="/physical-terminals" element={<ProtectedRoute data-id="element-298">
                <AppLayout data-id="element-299">
                  <PageTransitionWrapper data-id="element-300">
                    <PhysicalTerminals data-id="element-301" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-297" />
          <Route path="/softpos-terminals" element={<ProtectedRoute data-id="element-303">
                <AppLayout data-id="element-304">
                  <PageTransitionWrapper data-id="element-305">
                    <SoftPosTerminals data-id="element-306" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-302" />
          <Route path="/connect" element={<ProtectedRoute data-id="element-308">
                <AppLayout data-id="element-309">
                  <PageTransitionWrapper data-id="element-310">
                    <Connect data-id="element-311" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-307" />
          <Route path="/webhooks/endpoints" element={<ProtectedRoute data-id="element-313">
                <AppLayout data-id="element-314">
                  <PageTransitionWrapper data-id="element-315">
                    <WebhookEndpoints data-id="element-316" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-312" />
          <Route path="/webhooks/logs" element={<ProtectedRoute data-id="element-318">
                <AppLayout data-id="element-319">
                  <PageTransitionWrapper data-id="element-320">
                    <WebhookLogs data-id="element-321" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-317" />
          <Route path="/automations" element={<ProtectedRoute data-id="element-323">
                <AppLayout data-id="element-324">
                  <PageTransitionWrapper data-id="element-325">
                    <AutomationsList data-id="element-326" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-322" />
          <Route path="/automations/new" element={<ProtectedRoute data-id="element-328">
                <AppLayout data-id="element-329">
                  <PageTransitionWrapper data-id="element-330">
                    <AutomationBuilder data-id="element-331" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-327" />
          <Route path="/automations/templates" element={<ProtectedRoute data-id="element-333">
                <AppLayout data-id="element-334">
                  <PageTransitionWrapper data-id="element-335">
                    <AutomationTemplates data-id="element-336" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-332" />
          <Route path="/automations/runs" element={<ProtectedRoute data-id="element-338">
                <AppLayout data-id="element-339">
                  <PageTransitionWrapper data-id="element-340">
                    <AutomationRuns data-id="element-341" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-337" />
          <Route path="/automations/:id" element={<ProtectedRoute data-id="element-343">
                <AppLayout data-id="element-344">
                  <PageTransitionWrapper data-id="element-345">
                    <AutomationBuilder data-id="element-346" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-342" />
          <Route path="/tutorials" element={<ProtectedRoute data-id="element-348">
                <AppLayout data-id="element-349">
                  <PageTransitionWrapper data-id="element-350">
                    <Tutorials data-id="element-351" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-347" />
          <Route path="/tutorial/:id" element={<ProtectedRoute data-id="element-353">
                <AppLayout data-id="element-354">
                  <PageTransitionWrapper data-id="element-355">
                    <TutorialDetail data-id="element-356" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-352" />
          <Route path="/orders" element={<ProtectedRoute data-id="element-358">
                <AppLayout data-id="element-359">
                  <PageTransitionWrapper data-id="element-360">
                    <Orders data-id="element-361" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-357" />
          <Route path="/order-detail/:id" element={<ProtectedRoute data-id="element-363">
                <AppLayout data-id="element-364">
                  <PageTransitionWrapper data-id="element-365">
                    <OrderDetail data-id="element-366" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-362" />
          <Route path="/shops" element={<ProtectedRoute data-id="element-368">
                <AppLayout data-id="element-369">
                  <PageTransitionWrapper data-id="element-370">
                    <Shops data-id="element-371" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-367" />
          <Route path="/shifts" element={<ProtectedRoute data-id="element-373">
                <AppLayout data-id="element-374">
                  <PageTransitionWrapper data-id="element-375">
                    <ShiftsList data-id="element-376" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-372" />
          <Route path="/shifts/:id" element={<ProtectedRoute data-id="element-378">
                <AppLayout data-id="element-379">
                  <PageTransitionWrapper data-id="element-380">
                    <ShiftDetail data-id="element-381" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-377" />
          <Route path="/shop-details" element={<ProtectedRoute data-id="element-383">
                <AppLayout data-id="element-384">
                  <PageTransitionWrapper data-id="element-385">
                    <ShopDetails data-id="element-386" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-382" />
          <Route path="/shop-settings/:shopId" element={<ProtectedRoute data-id="element-388">
                <AppLayout data-id="element-389">
                  <PageTransitionWrapper data-id="element-390">
                    <ShopSettings data-id="element-391" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-387" />
          <Route path="/shop-settings" element={<ProtectedRoute data-id="element-393">
                <AppLayout data-id="element-394">
                  <PageTransitionWrapper data-id="element-395">
                    <ShopSettings data-id="element-396" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-392" />
          <Route path="/product-detail/:productId" element={<ProtectedRoute data-id="element-398">
                <AppLayout data-id="element-399">
                  <PageTransitionWrapper data-id="element-400">
                    <ProductDetail data-id="element-401" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-397" />
          <Route path="/product-edit/:productId" element={<ProtectedRoute data-id="element-403">
                <AppLayout data-id="element-404">
                  <PageTransitionWrapper data-id="element-405">
                    <ProductEdit data-id="element-406" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-402" />
          <Route path="/campaigns" element={<ProtectedRoute data-id="element-408">
                <AppLayout data-id="element-409">
                  <PageTransitionWrapper data-id="element-410">
                    <Campaigns data-id="element-411" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-407" />
          <Route path="/campaign-details" element={<ProtectedRoute data-id="element-413">
                <AppLayout data-id="element-414">
                  <PageTransitionWrapper data-id="element-415">
                    <CampaignDetails data-id="element-416" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-412" />
          <Route path="/campaign-form" element={<ProtectedRoute data-id="element-418">
                <AppLayout data-id="element-419">
                  <PageTransitionWrapper data-id="element-420">
                    <CampaignForm data-id="element-421" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-417" />
          <Route path="/property-information" element={<ProtectedRoute data-id="element-423">
                <AppLayout data-id="element-424">
                  <PageTransitionWrapper data-id="element-425">
                    <PropertyInformation data-id="element-426" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-422" />
          <Route path="/rooms" element={<ProtectedRoute data-id="element-428">
                <AppLayout data-id="element-429">
                  <PageTransitionWrapper data-id="element-430">
                    <Rooms data-id="element-431" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-427" />
          <Route path="/add-room" element={<ProtectedRoute data-id="element-433">
                <AppLayout data-id="element-434">
                  <PageTransitionWrapper data-id="element-435">
                    <AddRoom data-id="element-436" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-432" />
          <Route path="/edit-room" element={<ProtectedRoute data-id="element-438">
                <AppLayout data-id="element-439">
                  <PageTransitionWrapper data-id="element-440">
                    <EditRoom data-id="element-441" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-437" />
          <Route path="/room-detail" element={<ProtectedRoute data-id="element-443">
                <AppLayout data-id="element-444">
                  <PageTransitionWrapper data-id="element-445">
                    <RoomDetail data-id="element-446" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-442" />
          <Route path="/inventory" element={<ProtectedRoute data-id="element-448">
                <AppLayout data-id="element-449">
                  <PageTransitionWrapper data-id="element-450">
                    <Inventory data-id="element-451" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-447" />
          <Route path="/rates" element={<ProtectedRoute data-id="element-453">
                <AppLayout data-id="element-454">
                  <PageTransitionWrapper data-id="element-455">
                    <Rates data-id="element-456" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-452" />
          <Route path="/reservations" element={<ProtectedRoute data-id="element-458">
                <AppLayout data-id="element-459">
                  <PageTransitionWrapper data-id="element-460">
                    <Reservations data-id="element-461" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-457" />
          <Route path="/reservation-detail" element={<ProtectedRoute data-id="element-463">
                <AppLayout data-id="element-464">
                  <PageTransitionWrapper data-id="element-465">
                    <ReservationDetail data-id="element-466" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-462" />
          <Route path="/invoices" element={<ProtectedRoute data-id="element-468">
                <AppLayout data-id="element-469">
                  <PageTransitionWrapper data-id="element-470">
                    <Invoices data-id="element-471" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-467" />
          <Route path="/invoice-detail/:id" element={<ProtectedRoute data-id="element-473">
                <AppLayout data-id="element-474">
                  <PageTransitionWrapper data-id="element-475">
                    <InvoiceDetail data-id="element-476" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-472" />
          <Route path="/invoice-form" element={<ProtectedRoute data-id="element-478">
                <AppLayout data-id="element-479">
                  <PageTransitionWrapper data-id="element-480">
                    <InvoiceForm data-id="element-481" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-477" />
          <Route path="/customers" element={<ProtectedRoute data-id="element-483">
                <AppLayout data-id="element-484">
                  <PageTransitionWrapper data-id="element-485">
                    <Customers data-id="element-486" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-482" />
          <Route path="/customer-detail/:id" element={<ProtectedRoute data-id="element-488">
                <AppLayout data-id="element-489">
                  <PageTransitionWrapper data-id="element-490">
                    <CustomerDetail data-id="element-491" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-487" />
          <Route path="/customer-form" element={<ProtectedRoute data-id="element-493">
                <AppLayout data-id="element-494">
                  <PageTransitionWrapper data-id="element-495">
                    <CustomerForm data-id="element-496" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-492" />
          <Route path="/sales" element={<ProtectedRoute data-id="element-498">
                <AppLayout data-id="element-499">
                  <PageTransitionWrapper data-id="element-500">
                    <Sales data-id="element-501" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-497" />
          <Route path="/sale-detail/:id" element={<ProtectedRoute data-id="element-503">
                <AppLayout data-id="element-504">
                  <PageTransitionWrapper data-id="element-505">
                    <SaleDetail data-id="element-506" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-502" />
          <Route path="/balance" element={<ProtectedRoute data-id="element-508">
                <AppLayout data-id="element-509">
                  <PageTransitionWrapper data-id="element-510">
                    <BalanceSummary data-id="element-511" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-507" />
          <Route path="/balance-activity" element={<ProtectedRoute data-id="element-513">
                <AppLayout data-id="element-514">
                  <PageTransitionWrapper data-id="element-515">
                    <BalanceActivity data-id="element-516" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-512" />
          <Route path="/balance-activity/:id" element={<ProtectedRoute data-id="element-518">
                <AppLayout data-id="element-519">
                  <PageTransitionWrapper data-id="element-520">
                    <BalanceActivityDetail data-id="element-521" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-517" />
          <Route path="/payouts" element={<ProtectedRoute data-id="element-523">
                <AppLayout data-id="element-524">
                  <PageTransitionWrapper data-id="element-525">
                    <Payouts data-id="element-526" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-522" />
          <Route path="/payouts/:id" element={<ProtectedRoute data-id="element-528">
                <AppLayout data-id="element-529">
                  <PageTransitionWrapper data-id="element-530">
                    <PayoutDetail data-id="element-531" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-527" />
          <Route path="/suppliers" element={<ProtectedRoute data-id="element-533">
                <AppLayout data-id="element-534">
                  <PageTransitionWrapper data-id="element-535">
                    <Suppliers data-id="element-536" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-532" />
          <Route path="/supplier-detail/:id" element={<ProtectedRoute data-id="element-538">
                <AppLayout data-id="element-539">
                  <PageTransitionWrapper data-id="element-540">
                    <SupplierDetail data-id="element-541" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-537" />
          <Route path="/supplier-form" element={<ProtectedRoute data-id="element-543">
                <AppLayout data-id="element-544">
                  <PageTransitionWrapper data-id="element-545">
                    <SupplierForm data-id="element-546" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-542" />
          <Route path="/supplier-bulk-upload" element={<ProtectedRoute data-id="element-548">
                <AppLayout data-id="element-549">
                  <PageTransitionWrapper data-id="element-550">
                    <BulkSupplierUpload data-id="element-551" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-547" />
          <Route path="/bill-bulk-upload" element={<ProtectedRoute data-id="element-553">
                <AppLayout data-id="element-554">
                  <PageTransitionWrapper data-id="element-555">
                    <BulkBillUpload data-id="element-556" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-552" />
          <Route path="/reconciliation" element={<ProtectedRoute data-id="element-558">
                <AppLayout data-id="element-559">
                  <PageTransitionWrapper data-id="element-560">
                    <Reconciliation data-id="element-561" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-557" />
          <Route path="/bills" element={<ProtectedRoute data-id="element-563">
                <AppLayout data-id="element-564">
                  <PageTransitionWrapper data-id="element-565">
                    <Bills data-id="element-566" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-562" />
          <Route path="/bill-detail/:id" element={<ProtectedRoute data-id="element-568">
                <AppLayout data-id="element-569">
                  <PageTransitionWrapper data-id="element-570">
                    <BillDetail data-id="element-571" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-567" />
          <Route path="/bill-form" element={<ProtectedRoute data-id="element-573">
                <AppLayout data-id="element-574">
                  <PageTransitionWrapper data-id="element-575">
                    <BillForm data-id="element-576" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-572" />
          <Route path="/bill-approval/:id" element={<ProtectedRoute data-id="element-578">
                <AppLayout data-id="element-579">
                  <PageTransitionWrapper data-id="element-580">
                    <BillApprovalWizard data-id="element-581" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-577" />
          <Route path="/bill-financing/:id" element={<ProtectedRoute data-id="element-583">
                <AppLayout data-id="element-584">
                  <PageTransitionWrapper data-id="element-585">
                    <BillFinancingWizard data-id="element-586" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-582" />
          <Route path="/disputes" element={<ProtectedRoute data-id="element-588">
                <AppLayout data-id="element-589">
                  <PageTransitionWrapper data-id="element-590">
                    <Disputes data-id="element-591" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-587" />
          <Route path="/dispute-detail/:id" element={<ProtectedRoute data-id="element-593">
                <AppLayout data-id="element-594">
                  <PageTransitionWrapper data-id="element-595">
                    <DisputeDetail data-id="element-596" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-592" />
          <Route path="/disputes/:id" element={<ProtectedRoute data-id="element-598">
                <AppLayout data-id="element-599">
                  <PageTransitionWrapper data-id="element-600">
                    <DisputeDetail data-id="element-601" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-597" />
          <Route path="/disputes/:id/challenge" element={<ProtectedRoute data-id="element-603">
                <ChallengeDispute data-id="element-604" />
              </ProtectedRoute>} data-id="element-602" />
          <Route path="/refunds" element={<ProtectedRoute data-id="element-606">
                <AppLayout data-id="element-607">
                  <PageTransitionWrapper data-id="element-608">
                    <Refunds data-id="element-609" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-605" />
          <Route path="/refund-detail/:id" element={<ProtectedRoute data-id="element-611">
                <AppLayout data-id="element-612">
                  <PageTransitionWrapper data-id="element-613">
                    <RefundDetail data-id="element-614" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-610" />
          <Route path="/custom-metrics" element={<ProtectedRoute data-id="element-616">
                <AppLayout data-id="element-617">
                  <PageTransitionWrapper data-id="element-618">
                    <CustomMetrics data-id="element-619" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-615" />
          <Route path="/nadi" element={<ProtectedRoute data-id="element-621">
                <AppLayout data-id="element-622">
                  <PageTransitionWrapper data-id="element-623">
                    <Nadi data-id="element-624" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-620" />
          <Route path="/reports" element={<ProtectedRoute data-id="element-626">
                <AppLayout data-id="element-627">
                  <PageTransitionWrapper data-id="element-628">
                    <Reports data-id="element-629" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-625" />
          <Route path="/card-monitoring" element={<ProtectedRoute data-id="element-631">
                <AppLayout data-id="element-632">
                  <PageTransitionWrapper data-id="element-633">
                    <CardMonitoringPrograms data-id="element-634" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-630" />
          <Route path="/card-monitoring/vamp" element={<ProtectedRoute data-id="element-636">
                <AppLayout data-id="element-637">
                  <PageTransitionWrapper data-id="element-638">
                    <VampDetail data-id="element-639" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-635" />
          <Route path="/payment-analytics" element={<ProtectedRoute data-id="element-641">
                <AppLayout data-id="element-642">
                  <PageTransitionWrapper data-id="element-643">
                    <PaymentAnalytics data-id="element-644" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-640" />
          <Route path="/activity-logs" element={<ProtectedRoute data-id="element-646">
                <AppLayout data-id="element-647">
                  <PageTransitionWrapper data-id="element-648">
                    <ActivityLogs data-id="element-649" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-645" />
          <Route path="/requests" element={<ProtectedRoute data-id="element-651">
                <AppLayout data-id="element-652">
                  <PageTransitionWrapper data-id="element-653">
                    <Requests data-id="element-654" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-650" />
          <Route path="/requests/:id" element={<ProtectedRoute data-id="element-656">
                <AppLayout data-id="element-657">
                  <PageTransitionWrapper data-id="element-658">
                    <RequestDetail data-id="element-659" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-655" />
        </Routes>
      </Suspense>
    </BrowserRouter>;
}